import React, { useMemo } from "react";
import { Button, Col, Empty, Popconfirm, Row } from "antd";
import { useHistory, useParams } from "react-router";
import { Flex } from "css/styles";
import { ArrowLeftOutlined, SyncOutlined } from "@ant-design/icons";
import { useQuery, useMutation } from "react-query";
import { fetchInventoryTransfer, cancelInventoryTransfer } from "../../../api/transfer";
import { Card } from "./styles";
import { TransferDetails } from "./TransferDetails";
import { TransferItems } from "./TransferItems";
import { TransferActivity } from "./TransferActivity";
import { generateSerial } from "../InventoryTransferIndexPage/helper";
import useUser from "hooks/reduxContainer/_common/useUser";
import { useOrganizationContext } from "contextProviders/organization/OrganizationProvider";
import * as S from "../InventoryTransferIndexPage/styles";
import { useFacilityLookup } from "hooks/api/common/useFacilityIndex";
import { MapContainer } from "../InventoryTransferCreatePage/styles";
import { Map } from "../InventoryTransferCreatePage/Map";
import { isEmpty } from "lodash-es";

export const InventoryTransferDetailsPage: React.FC = () => {
  const history = useHistory();
  const { data: org } = useOrganizationContext();
  const { transferId } = useParams<{ transferId: string }>();
  const { user } = useUser();
  const isAdmin = user?.role === "is_admin";

  const facilityLookup = useFacilityLookup();

  const { data: transfer, ...transferQuery } = useQuery(
    ["transfer", transferId],
    () => fetchInventoryTransfer(transferId),
    { enabled: !!transferId }
  );

  const locations = useMemo(() => {
    if (!transfer || isEmpty(transfer) || isEmpty(facilityLookup)) return []; // test if working
    const facilities = [transfer.from_facility, transfer.to_facility];
    return facilities.map((f) => ({
      lat: facilityLookup[f].location.latitude,
      lng: facilityLookup[f].location.longitude,
      address: facilityLookup[f].address.city,
    }));
  }, [transfer, facilityLookup]);

  const { mutateAsync: updateTransfer } = useMutation(cancelInventoryTransfer, {
    onSuccess: () => transferQuery.refetch(),
  });

  const RefreshButton = <Button type="text" icon={<SyncOutlined />} />;
  const CancelButton = (
    <Popconfirm
      title="Are you sure to cancel this transfer?"
      onConfirm={() => updateTransfer(transferId)}
      okText="Yes"
      cancelText="No"
    >
      <Button type="primary" disabled={transfer?.status !== "requested"} danger>
        Cancel
      </Button>
    </Popconfirm>
  );

  return (
    <div className="page-container" id="inventory-transfer-index-page">
      <Flex direction="column" align="start" gap="1.5em">
        <S.PageHeader direction="vertical">
          <Flex dir="row" justify="space-between">
            <Flex gap={"1em"}>
              <S.Button icon={<ArrowLeftOutlined />} onClick={() => history.goBack()} />
              <S.PageTitle>{`Transfer ${transfer ? generateSerial(transfer.id.toString()) : ""}`}</S.PageTitle>
            </Flex>
            <Flex gap={"1em"}>
              {RefreshButton}
              {CancelButton}
            </Flex>
          </Flex>
        </S.PageHeader>

        <Card>
          <Row gutter={[16, 24]}>
            <Col span={12}>{transfer && <TransferDetails transfer={transfer} loading={transferQuery.isLoading} />}</Col>
            <Col span={12}>
              <MapContainer height="100%">
                {transfer ? (
                  <Map mapId={`${transfer.from_facility}-${transfer.to_facility}`} markers={locations} />
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </MapContainer>
            </Col>
            <Col span={24}>
              <TransferItems loading={transferQuery.isLoading} items={transfer?.items || []} orgId={org?.id || ""} />
            </Col>
            <Col span={24}>
              {isAdmin && (
                <TransferActivity
                  loading={transferQuery.isLoading}
                  orderId={transfer?.order_id || ""}
                  activities={transfer?.activities || []}
                />
              )}
            </Col>
          </Row>
        </Card>
      </Flex>
    </div>
  );
};
